import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Calendar, X, Check, Clock } from 'lucide-react';

/**
 * AutoGenerationPrompt - Modal de confirmation pour la génération automatique
 */
export function AutoGenerationPrompt({ isOpen, onConfirm, onCancel, onPostpone, settings }) {
  if (!isOpen) return null;

  const templateName = settings?.template === 'minimal' ? 'Minimal' :
                       settings?.template === 'balanced' ? 'Équilibré' :
                       settings?.template === 'structured' ? 'Structuré' :
                       settings?.template === 'intense' ? 'Intense' : 'Personnalisé';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-sm bg-[#0e0e14] border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl"
        >
          {/* Header avec animation */}
          <div className="relative px-6 pt-6 pb-4 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
              className="w-16 h-16 mx-auto bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center mb-4"
            >
              <Sparkles className="w-8 h-8 text-cyan-400" />
            </motion.div>

            <h2 className="text-xl font-bold text-white mb-2">Nouvelle semaine !</h2>
            <p className="text-sm text-zinc-400">
              C'est le début d'une nouvelle semaine. Veux-tu générer ton planning ?
            </p>
          </div>

          {/* Aperçu */}
          <div className="px-6 pb-4">
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Template
                </span>
                <span className="text-zinc-200 font-medium">{templateName}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400">Jours inclus</span>
                <span className="text-zinc-200 font-medium">
                  {settings?.includeWeekends ? 'Lun - Dim' : 'Lun - Ven'}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 space-y-2">
            <button
              onClick={onConfirm}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-medium rounded-xl transition-all"
            >
              <Check className="w-5 h-5" />
              Générer ma semaine
            </button>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={onPostpone}
                className="flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium rounded-xl transition-all"
              >
                <Clock className="w-4 h-4" />
                Plus tard
              </button>
              <button
                onClick={onCancel}
                className="flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium rounded-xl transition-all"
              >
                <X className="w-4 h-4" />
                Non merci
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default AutoGenerationPrompt;
